import {
  OutboxEventStatus,
  enumOutboxEventStatus,
} from '@rematter/frontend-lib-api-client';
import { Col, Row, Badge, humanReadableDateTime } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';

import { BillEventDetail } from './bill';
import { InvoiceEventDetail } from './invoice';

import {
  $remoteFinanceOutboxEvents,
  useSelectedFinanceEventId,
} from '@/audit-log/_state/financeEvents.state';
import { useTranslations } from '@/audit-log/i18n/useTranslations';
import { getLocale } from '~/shared/utils/locale';

const getStatusColor = (status: OutboxEventStatus) => {
  switch (status) {
    case enumOutboxEventStatus.Processed:
      return 'success';
    case enumOutboxEventStatus.Pending:
      return 'warning';
    case enumOutboxEventStatus.Failed:
      return 'failure';
    case enumOutboxEventStatus.DeadLetter:
      return 'failure';
    default:
      return 'neutral';
  }
};

type MetadataRowProps = { label: string; value: string };

const MetadataRow = ({ label, value }: MetadataRowProps) => (
  <Row className="@MetadataRow justify-between py-1 text-sm">
    <span className="text-neutral/60">{label}</span>
    <span className="font-mono text-xs">{value}</span>
  </Row>
);

type AggregateType = 'Bill' | 'Invoice';

const getAggregateTypeFromEventName = (
  eventName: string,
): AggregateType | null => {
  if (eventName.startsWith('Bill')) return 'Bill';
  if (eventName.startsWith('Invoice')) return 'Invoice';
  return null;
};

type EventPayloadDetailProps = {
  aggregateType: AggregateType;
  payload: string;
};

const EventPayloadDetail = ({
  aggregateType,
  payload,
}: EventPayloadDetailProps) => {
  switch (aggregateType) {
    case 'Bill':
      return <BillEventDetail payload={payload} />;
    case 'Invoice':
      return <InvoiceEventDetail payload={payload} />;
  }
};

export const FinanceEventDetail = () => {
  const t = useTranslations();
  const [selectedEventId] = useSelectedFinanceEventId();

  const { data } = useAtomValue($remoteFinanceOutboxEvents);

  const selectedEvent = data?.events.find((e) => e.id === selectedEventId);

  if (!selectedEvent) {
    return (
      <Col className="@FinanceEventDetail flex-1 items-center justify-center bg-neutral/2 text-neutral/40">
        <span>{t.financeEventsView.detail.empty}</span>
      </Col>
    );
  }

  const aggregateType = getAggregateTypeFromEventName(selectedEvent.eventName);

  return (
    <Col className="@FinanceEventDetail flex-1 overflow-hidden bg-neutral/2">
      <Col className="flex-1 overflow-y-auto p-4" gap4>
        <Col gap2>
          <Row className="items-center justify-between">
            <span className="text-lg font-medium">
              {selectedEvent.eventName}
            </span>
            <Badge
              color={getStatusColor(selectedEvent.status as OutboxEventStatus)}
            >
              {selectedEvent.status}
            </Badge>
          </Row>
        </Col>

        <Col gap1 className="rounded-md border border-neutral/10 bg-white p-3">
          <span className="mb-2 text-sm font-medium text-neutral/80">
            {t.financeEventsView.detail.metadata.title}
          </span>
          <MetadataRow
            label={t.financeEventsView.detail.metadata.eventId}
            value={selectedEvent.id}
          />
          <MetadataRow
            label={t.financeEventsView.detail.metadata.aggregateId}
            value={
              selectedEvent.metadata.aggregateId ??
              t.financeEventsView.detail.notAvailable
            }
          />
          <MetadataRow
            label={t.financeEventsView.detail.metadata.topic}
            value={selectedEvent.topic}
          />
          <MetadataRow
            label={t.financeEventsView.detail.metadata.version}
            value={String(selectedEvent.version)}
          />
          <MetadataRow
            label={t.financeEventsView.detail.metadata.retryCount}
            value={String(selectedEvent.retryCount)}
          />
          <MetadataRow
            label={t.financeEventsView.detail.metadata.createdAt}
            value={humanReadableDateTime(selectedEvent.timestamps.createdAt, {
              locale: getLocale(),
              includeYear: true,
              includeSeconds: true,
            })}
          />
          <MetadataRow
            label={t.financeEventsView.detail.metadata.createdBy}
            value={
              selectedEvent.createdByUser
                ? `${(selectedEvent.createdByUser as { firstName?: string; lastName?: string }).firstName ?? ''} ${(selectedEvent.createdByUser as { firstName?: string; lastName?: string }).lastName ?? ''}`.trim() ||
                  t.financeEventsView.detail.notAvailable
                : t.financeEventsView.detail.notAvailable
            }
          />
          {selectedEvent.lastRetry && (
            <MetadataRow
              label={t.financeEventsView.detail.metadata.lastRetry}
              value={humanReadableDateTime(selectedEvent.lastRetry, {
                locale: getLocale(),
                includeYear: true,
                includeSeconds: true,
              })}
            />
          )}
        </Col>

        {aggregateType ? (
          <EventPayloadDetail
            aggregateType={aggregateType}
            payload={selectedEvent.payload as string}
          />
        ) : (
          <Col
            gap1
            className="flex-1 rounded-md border border-neutral/10 bg-white"
          >
            <span className="border-b border-neutral/10 p-3 text-sm font-medium text-neutral/80">
              {t.financeEventsView.detail.payload}
            </span>
            <pre className="flex-1 overflow-auto p-3 font-mono text-xs">
              {(() => {
                try {
                  return JSON.stringify(
                    JSON.parse(selectedEvent.payload as string),
                    null,
                    2,
                  );
                } catch {
                  return selectedEvent.payload;
                }
              })()}
            </pre>
          </Col>
        )}
      </Col>
    </Col>
  );
};
